import { i18n } from "@tellescope/i18n";
import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Dispatch, SetStateAction } from "react";
import { createStore } from "./utils";

/** URL viewId used by `/vitals/views/:viewId` for the unfiltered all-vitals view. */
export const ALL_VITALS_VIEW_ID = "all";

const getDefaultTitle = () => i18n.t("Default View", { ns: "vitals" });

export const DEFAULT_VITALS_VIEW = {
  id: "default",
  businessId: "",
  updatedAt: new Date(0),
  creator: "",
  title: getDefaultTitle(),
  page: "Vitals",
  columns: [
    {
      field: "Enduser",
    },
    {
      field: "Timestamp",
    },
    {
      field: "Flags",
    },
    {
      field: "Values",
    },
    {
      field: "Classification",
    },
    {
      field: "Source",
    },
    {
      field: "Reviewed",
    },
    {
      field: "Visible",
    },
  ],
  createdAt: new Date(0),
} as const satisfies TellescopeModelFromName<"table_views">;

interface VitalsDefaultViewStore {
  defaultViewByUserId: Record<string, TellescopeModelFromName<"table_views">>;
  setUserDefaultView: (
    userId: string,
    newVal: Parameters<Dispatch<SetStateAction<TellescopeModelFromName<"table_views">>>>[0]
  ) => void;
  /** URL `:viewId` of the last `/vitals/views/:viewId` the user visited. */
  lastSelectedViewId: string;
  setLastSelectedViewId: (viewId: string) => void;
}

export const { useStore: useVitalsDefaultViewStore, useShallowStore: useShallowVitalsDefaultViewStore } =
  createStore<VitalsDefaultViewStore>(
    (set) => ({
      defaultViewByUserId: {},
      setUserDefaultView: (userId, newVal) =>
        set((state) => {
          const currentValue = state.defaultViewByUserId[userId] ?? DEFAULT_VITALS_VIEW;

          const resolvedVal = typeof newVal === "function" ? newVal(currentValue) : newVal;

          return {
            ...state,
            defaultViewByUserId: {
              ...state.defaultViewByUserId,
              [userId]: resolvedVal,
            },
          };
        }),
      lastSelectedViewId: ALL_VITALS_VIEW_ID,
      setLastSelectedViewId: (viewId) => set({ lastSelectedViewId: viewId }),
    }),
    {
      persist: "localStorage",
      name: "DEFAULT_VITALS_TABLE_VIEW",
    }
  );
